import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { useUploadModal } from "../../hooks/upload/useUploadModal";
import { useUppyUpload } from "../../hooks/upload/useUppyUpload";
import { useUppyDashboard } from "../../hooks/upload/useUppyDashboard";
import { AttributePanel } from "./UploadModalAttributePanel";
import type { Category } from "@/services/api/categories/types";
import { getCategoryLabel } from "@/features/categories/utils/categoryUtils";
import { useUppyContext } from "@/app/providers/useUppyContext";
import { galleryKeys } from "../../hooks/gallery/useGallery";

interface UploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: Category;
}

export function UploadModal({
  open,
  onOpenChange,
  category,
}: UploadModalProps) {
  const queryClient = useQueryClient();
  const { uppy } = useUppyContext();
  const uploadCompletedRef = useRef(false);

  const {
    attributeValues,
    requiredAttributes,
    optionalAttributes,
    categoryAttributes,
    isLoading: isLoadingAttributes,
    allRequiredFilled,
    handleAttributeChange,
    resetState,
    selectedTagIds,
    selectedTags,
    filteredAvailableTags,
    tagsLoading,
    tagSearchQuery,
    setTagSearchQuery,
    handleTagToggle,
  } = useUploadModal({ category, enabled: open });

  const categoryId =
    typeof category.id === "number" ? category.id : Number(category.id);

  const handleUploadComplete = useCallback(() => {
    uploadCompletedRef.current = true;
  }, []);

  const { files, upload, cancelAll, reset, uploadProgress } = useUppyUpload({
    uppy,
    categoryId,
    attributeValues,
    tagIds: selectedTagIds,
    onUploadComplete: handleUploadComplete,
    enabled: open && allRequiredFilled,
  });

  const dashboardRef = useUppyDashboard({
    uppy,
    open,
    allRequiredFilled,
  });

  const isUploadComplete = useMemo(() => {
    if (uploadProgress.length === 0) return false;
    return (
      uploadProgress.length > 0 &&
      uploadProgress.every((file) => file.status === "complete")
    );
  }, [uploadProgress]);

  useEffect(() => {
    if (open) {
      reset();
      resetState();
      uploadCompletedRef.current = false;
    }
  }, [open, reset, resetState]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        if (uploadCompletedRef.current) {
          queryClient.invalidateQueries({
            queryKey: [...galleryKeys.lists(), categoryId],
          });
          uploadCompletedRef.current = false;
        }
        cancelAll();
        reset();
        resetState();
      }
      onOpenChange(newOpen);
    },
    [onOpenChange, queryClient, categoryId, cancelAll, reset, resetState]
  );

  const handleClose = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[1600px] w-[95vw] h-[90vh] max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-8 pt-8 pb-6 shrink-0">
          <DialogTitle className="text-xl font-semibold">
            Data Uploader - {getCategoryLabel(category)}
          </DialogTitle>
          <DialogDescription>
            Upload files with required attributes for this category.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 gap-8 overflow-hidden min-h-0 px-8 pb-6">
          <AttributePanel
            requiredAttributes={requiredAttributes}
            optionalAttributes={optionalAttributes}
            categoryAttributes={categoryAttributes}
            isLoading={isLoadingAttributes}
            attributeValues={attributeValues}
            onAttributeChange={handleAttributeChange}
            selectedTags={selectedTags}
            filteredAvailableTags={filteredAvailableTags}
            tagsLoading={tagsLoading}
            tagSearchQuery={tagSearchQuery}
            onTagSearchChange={setTagSearchQuery}
            onTagToggle={handleTagToggle}
            hasAvailableTags={filteredAvailableTags.length > 0 || selectedTags.length > 0}
          />
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {!allRequiredFilled ? (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-sm text-ink-600 text-center px-8">
                  Fill in all the required attributes to continue with the
                  upload process.
                </p>
              </div>
            ) : (
              <div
                ref={dashboardRef}
                className="flex-1 overflow-hidden w-full"
                style={{ minHeight: 0, width: "100%" }}
              />
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-6 border-t border-grid-200 px-8 pb-8 shrink-0">
          {!isUploadComplete && (
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          )}
          {allRequiredFilled && files.length > 0 && (
            <>
              {isUploadComplete ? (
                <Button onClick={handleClose}>Done</Button>
              ) : (
                <Button onClick={upload}>
                  Upload {files.length} file{files.length > 1 ? "s" : ""}
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
